import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogIn, UserPlus, Mail, Lock, User, Phone, MapPin, Building2, Home, Hash, Globe, MessageCircle } from "lucide-react";

const ALL_COUNTRIES = ["Kosovë", "Shqipëri", "Maqedoni e Veriut", "Mal i Zi"];
const CONTACT_METHODS = [
  { value: "Telefon", label: "Telefon", icon: Phone },
  { value: "WhatsApp", label: "WhatsApp", icon: MessageCircle },
  { value: "Viber", label: "Viber", icon: MessageCircle },
  { value: "Email", label: "Email", icon: Mail },
];

export default function Login() {
  const { user, login, register } = useApp();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    company_name: "", street: "", house_number: "", postal_code: "",
    city: "", country: "Kosovë", preferred_contact: "Telefon",
  });
  const [loading, setLoading] = useState(false);
  const [enabledCountries, setEnabledCountries] = useState(ALL_COUNTRIES);

  useEffect(() => {
    api.get("/settings").then(r => {
      if (r.data.shipping_fees && Object.keys(r.data.shipping_fees).length > 0) {
        const enabled = ALL_COUNTRIES.filter(c => Object.prototype.hasOwnProperty.call(r.data.shipping_fees, c));
        if (enabled.length > 0) {
          setEnabledCountries(enabled);
          setForm(prev => enabled.includes(prev.country) ? prev : { ...prev, country: enabled[0] });
        }
      }
    }).catch(() => {});
  }, []);

  const redirectFor = (u) => u?.role === "admin" ? "/admin" : u?.role === "worker" ? "/arka" : "/profili";
  useEffect(() => { if (user) nav(redirectFor(user), { replace: true }); }, [user, nav]);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const u = mode === "login" ? await login(form.email, form.password) : await register(form);
      toast.success(mode === "login" ? "Mirë se erdhët!" : "Llogaria u krijua me sukses!");
      nav(redirectFor(u));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (mode === "login" ? "Email ose fjalëkalim i gabuar" : "Regjistrimi dështoi"));
    }
    setLoading(false);
  };

  const Icon = mode === "login" ? LogIn : UserPlus;
  const Field = ({ icon: I, ...p }) => (
    <div className="relative">
      <I size={16} className="absolute left-3 top-3.5 text-neutral-500 z-10" />
      <input {...p} className="w-full bg-[#131E30] border border-[#1F2B44] pl-10 pr-3 py-3 focus:border-[#FFD600] outline-none" />
    </div>
  );

  return (
    <div className="max-w-md mx-auto px-4 py-6 sm:py-10">
      <div className="hg-card p-5 sm:p-6">
        <div className="grid grid-cols-2 gap-2 mb-6 p-1 bg-[#131E30] border border-[#1F2B44] rounded">
          <button onClick={() => setMode("login")} data-testid="tab-login"
            className={`py-2.5 text-xs uppercase tracking-widest font-bold ${mode === "login" ? "bg-[#FFD600] text-black" : "text-neutral-400"}`}>Kyçu</button>
          <button onClick={() => setMode("register")} data-testid="tab-register"
            className={`py-2.5 text-xs uppercase tracking-widest font-bold ${mode === "register" ? "bg-[#FFD600] text-black" : "text-neutral-400"}`}>Regjistrohu</button>
        </div>

        <div className="w-14 h-14 bg-[#FFD600] text-black flex items-center justify-center mb-4"><Icon size={26} /></div>
        <div className="font-display text-3xl uppercase mb-1">{mode === "login" ? "Kyçu" : "Regjistrohu"}</div>
        <div className="text-sm text-neutral-400 mb-6">
          {mode === "login" ? "Hyni te llogaria juaj për të parë porositë tuaja" : "Krijoni një llogari të re për të porositur më shpejt"}
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <Field icon={User} type="text" required placeholder="Emri dhe mbiemri" data-testid="reg-name" value={form.name} onChange={e => set("name", e.target.value)} />
          )}
          <Field icon={Mail} type="email" required placeholder="Email"
            data-testid={mode === "login" ? "login-email" : "reg-email"} value={form.email} onChange={e => set("email", e.target.value)} />
          <Field icon={Lock} type="password" required placeholder={mode === "register" ? "Fjalëkalimi (min. 6 karaktere)" : "Fjalëkalimi"}
            data-testid={mode === "login" ? "login-password" : "reg-password"} value={form.password} onChange={e => set("password", e.target.value)} />

          {mode === "register" && (
            <>
              <Field icon={Phone} type="tel" placeholder="Telefoni" data-testid="reg-phone" value={form.phone} onChange={e => set("phone", e.target.value)} />
              <Field icon={Building2} type="text" placeholder="Emri i firmës (opsional)" data-testid="reg-company" value={form.company_name} onChange={e => set("company_name", e.target.value)} />

              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-2">📍 Adresa</div>
                <div className="grid grid-cols-[2fr_1fr] gap-2">
                  <Field icon={Home} type="text" placeholder="Rruga" data-testid="reg-street" value={form.street} onChange={e => set("street", e.target.value)} />
                  <Field icon={Hash} type="text" placeholder="Nr." data-testid="reg-house" value={form.house_number} onChange={e => set("house_number", e.target.value)} />
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-2 mt-2">
                  <Field icon={Hash} type="text" placeholder="Kodi postar" data-testid="reg-postal" value={form.postal_code} onChange={e => set("postal_code", e.target.value)} />
                  <Field icon={MapPin} type="text" placeholder="Qyteti" data-testid="reg-city" value={form.city} onChange={e => set("city", e.target.value)} />
                </div>
                <div className="relative mt-2">
                  <Globe size={16} className="absolute left-3 top-3.5 text-neutral-500 z-10" />
                  <select value={form.country} onChange={e => set("country", e.target.value)} data-testid="reg-country"
                    className="w-full bg-[#131E30] border border-[#1F2B44] pl-10 pr-3 py-3 focus:border-[#FFD600] outline-none appearance-none">
                    {enabledCountries.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-2">💬 Si dëshironi t'ju kontaktojmë?</div>
                <div className="grid grid-cols-2 gap-2">
                  {CONTACT_METHODS.map(m => {
                    const active = form.preferred_contact === m.value;
                    return (
                      <button type="button" key={m.value} onClick={() => set("preferred_contact", m.value)}
                        data-testid={`reg-contact-${m.value.toLowerCase()}`}
                        className={`flex items-center justify-center gap-2 py-2.5 border text-xs uppercase tracking-wider transition-colors ${active ? "bg-[#FFD600] text-black border-[#FFD600]" : "border-[#1F2B44] text-neutral-400 hover:border-[#FFD600] hover:text-[#FFD600]"}`}>
                        <m.icon size={14} /> {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="text-[11px] text-neutral-500 pt-2">
                Duke u regjistruar, ju pranoni <span className="text-[#FFD600]">Kushtet</span> dhe <span className="text-[#FFD600]">Politikën e Privatësisë</span>.
              </div>
            </>
          )}
          <button disabled={loading} data-testid={mode === "login" ? "login-submit" : "reg-submit"}
            className="hg-btn-primary w-full py-3 uppercase text-sm disabled:opacity-40">
            {loading ? "..." : (mode === "login" ? "Kyçu" : "Krijo llogari")}
          </button>
        </form>

        <div className="mt-5 pt-4 border-t border-[#1F2B44] text-center text-xs text-neutral-400">
          {mode === "login" ? (
            <>Nuk keni llogari? <button onClick={() => setMode("register")} className="text-[#FFD600] hover:underline">Regjistrohu këtu</button></>
          ) : (
            <>Keni llogari? <button onClick={() => setMode("login")} className="text-[#FFD600] hover:underline">Kyçuni këtu</button></>
          )}
        </div>
      </div>
    </div>
  );
}
